import { useEffect, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export const TASK_META = {
  richiamo: { label: "Richiamo", add: "Aggiungi richiamo", statuses: [["da_fare", "Da fare", "#FEF3C7", "#92400E"], ["completato", "Completato", "#D1FAE5", "#065F46"]] },
  materiale: { label: "Materiale", add: "Aggiungi materiale", statuses: [["da_ordinare", "Da ordinare", "#FEE2E2", "#991B1B"], ["ordinato", "Ordinato", "#DBEAFE", "#1E40AF"], ["ricevuto", "Ricevuto", "#D1FAE5", "#065F46"], ["annullato", "Annullato", "#E2E8F0", "#475569"]] },
  preventivo: { label: "Preventivo da preparare", add: "Aggiungi preventivo", statuses: [["da_preparare", "Da preparare", "#FEF3C7", "#92400E"], ["pronto", "Pronto", "#DBEAFE", "#1E40AF"], ["inviato", "Inviato", "#D1FAE5", "#065F46"]] },
};
export const statusInfo = (kind, s) => TASK_META[kind].statuses.find((x) => x[0] === s) || TASK_META[kind].statuses[0];
export const nextStatus = (kind, s) => {
  const st = TASK_META[kind].statuses.filter((x) => x[0] !== "annullato"); const i = st.findIndex((x) => x[0] === s);
  return i >= 0 && i < st.length - 1 ? st[i + 1] : null;
};

const F = ({ label, children }) => <div><Label className="text-xs">{label}</Label>{children}</div>;

export default function TaskDialog({ open, kind, task, clients, onClose, onChanged }) {
  const [form, setForm] = useState({});
  const [confirmDel, setConfirmDel] = useState(false);
  const [sups, setSups] = useState([]);
  useEffect(() => { if (open && kind === "materiale") api.get("/suppliers").then((r) => setSups(r.data)).catch(() => {}); }, [open, kind]);
  const isNew = !task;
  useEffect(() => {
    if (open) { setConfirmDel(false); setForm(task ? { ...task } : { status: TASK_META[kind].statuses[0][0], date: kind === "richiamo" ? new Date().toLocaleDateString("sv-SE") : "" }); }
  }, [open, task, kind]);
  useEffect(() => {
    if (!open) { const t = setTimeout(() => { if (document.body.style.pointerEvents === "none") document.body.style.pointerEvents = ""; }, 200); return () => clearTimeout(t); }
  }, [open]);
  if (!kind) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    const body = { ...form, kind };
    try {
      if (isNew) await api.post("/tasks", body); else await api.put(`/tasks/${task.id}`, body);
      toast.success(isNew ? "Voce aggiunta" : "Modifiche salvate");
      if (["pronto", "inviato"].includes(form.status) && kind === "preventivo" && !task?.quote_id) toast.info("Creata una bozza nella sezione Preventivi");
      onChanged(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito"); }
  };
  const del = async () => {
    try { await api.delete(`/tasks/${task.id}`); toast.success("Voce eliminata"); onChanged(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Eliminazione non riuscita"); }
  };

  const clientSel = (
    <F label={kind === "preventivo" ? "Cliente" : "Cliente (facoltativo)"}>
      <Select value={form.client_id || "__none"} onValueChange={(v) => setForm({ ...form, client_id: v === "__none" ? "" : v })}>
        <SelectTrigger data-testid="task-client"><SelectValue /></SelectTrigger>
        <SelectContent onCloseAutoFocus={(e) => e.preventDefault()} className="max-h-72">
          <SelectItem value="__none">— Nessun cliente —</SelectItem>
          {clients.filter((c) => c.id).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </F>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="task-dialog">
        <DialogHeader><DialogTitle className="font-head">{isNew ? `Nuovo ${TASK_META[kind].label.toLowerCase()}` : TASK_META[kind].label}</DialogTitle><DialogDescription className="sr-only">Compila i dati e salva</DialogDescription></DialogHeader>
        <div className="space-y-3">
          {kind === "richiamo" && (<>
            <F label="Chi chiama chi"><Input data-testid="task-title" value={form.title || ""} onChange={set("title")} placeholder="es. Elia chiama Graziella" /></F>
            {clientSel}
            <div className="grid grid-cols-2 gap-3">
              <F label="Data"><Input data-testid="task-date" type="date" value={form.date || ""} onChange={set("date")} /></F>
              <F label="Ora"><Input data-testid="task-time" type="time" value={form.time || ""} onChange={set("time")} /></F>
            </div>
          </>)}
          {kind === "materiale" && (<>
            <F label="Materiale"><Input data-testid="task-material" value={form.material || ""} onChange={set("material")} placeholder="es. Telo acrilico 3x2 grigio" /></F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Quantità"><Input data-testid="task-quantity" value={form.quantity || ""} onChange={set("quantity")} placeholder="es. 2 pz" /></F>
              <F label="Fornitore">
                <Select value={form.supplier_id || "__none"} onValueChange={(v) => setForm({ ...form, supplier_id: v === "__none" ? "" : v })}>
                  <SelectTrigger data-testid="task-supplier-id"><SelectValue /></SelectTrigger>
                  <SelectContent onCloseAutoFocus={(e) => e.preventDefault()} className="max-h-72"><SelectItem value="__none">— Non in elenco —</SelectItem>{sups.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </F>
            </div>
            {!form.supplier_id && <F label="Fornitore (testo libero)"><Input data-testid="task-supplier" value={form.supplier || ""} onChange={set("supplier")} /></F>}
            <div className="grid grid-cols-2 gap-3">
              <F label="Importo ordine € (IVA escl.)"><Input data-testid="task-amount" type="number" step="any" inputMode="decimal" value={form.amount ?? ""} onChange={set("amount")} /></F>
              <F label="Pagamento previsto il"><Input data-testid="task-payment-due" type="date" value={form.payment_due || ""} onChange={set("payment_due")} /></F>
            </div>
            <p className="text-[11px] text-muted-foreground" data-testid="task-cf-info">
              {form.paid ? "Pagamento registrato: l'uscita risulta pagata nello storico del fornitore."
                : form.cashflow_id ? "Collegato a un'uscita prevista in Incassi: si aggiorna con l'ordine."
                : form.cashflow_detached ? "Uscita collegata eliminata da Incassi: cambia importo o fornitore per ricrearla."
                : "Con fornitore dall'elenco e importo, viene creata in automatico un'uscita prevista in Incassi (senza data di pagamento usa la data di oggi)."}
            </p>
            {clientSel}
          </>)}
          {kind === "preventivo" && (<>
            {clientSel}
            <F label="Cosa preparare"><Input data-testid="task-product" value={form.product || ""} onChange={set("product")} placeholder="es. Pergola bioclimatica 4x3" /></F>
            <F label="Scadenza"><Input data-testid="task-due" type="date" value={form.due_date || ""} onChange={set("due_date")} /></F>
          </>)}
          <F label="Note"><Textarea data-testid="task-notes" rows={3} value={form.notes || ""} onChange={set("notes")} /></F>
          <F label="Stato">
            <div className="flex flex-wrap gap-2">
              {TASK_META[kind].statuses.map(([v, l]) => (
                <button key={v} type="button" data-testid={`task-status-${v}`} onClick={() => setForm({ ...form, status: v })}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${form.status === v ? "bg-ink text-white border-ink" : "bg-white text-muted-foreground"}`}>{l}</button>
              ))}
            </div>
          </F>
          {kind === "preventivo" && task?.quote_id && <p className="text-xs text-emerald-700">Bozza già creata nella sezione Preventivi.</p>}
        </div>
        <DialogFooter className="flex-row gap-2 sm:justify-between">
          {!isNew && (confirmDel
            ? <Button variant="destructive" data-testid="task-delete-confirm" onClick={del}>Conferma eliminazione</Button>
            : <Button variant="outline" className="text-destructive border-destructive/40" data-testid="task-delete" onClick={() => setConfirmDel(true)}><Trash2 size={15} /></Button>)}
          <Button className="bg-brand hover:bg-[#A8472F] text-white flex-1 sm:flex-none" data-testid="task-save" onClick={save}>{isNew ? "Salva" : "Salva modifiche"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
